import { WorkflowType } from '@/constants/dispositions';
import type { FormField } from '@/types/formSchema';

import type { SampleFieldDefinition } from './excel';

/**
 * Separates repeated values inside a single campaign-file cell.
 *
 * A column addressing a leaf under a repeated (array) schema field may carry several
 * values: `cpt_codes.code` = "96413|96415" creates two `cpt_codes` elements. Every
 * populated column of the same repeated field must carry the SAME number of values —
 * the server rejects the row otherwise, rather than pairing values by guesswork.
 *
 * Must stay in sync with `MultiValueDelimiter` in `service/file_paths.go`.
 */
export const MULTI_VALUE_DELIMITER = '|';

/**
 * Marks a deliberately absent value inside a repeated cell: `96413|NA|96417` is three
 * elements whose middle code is unset. `N/A` is accepted too, case-insensitively.
 *
 * Only meaningful inside repeated columns — in a flat column `NA` stays literal text.
 * Must stay in sync with `emptyValueMarkers` in `service/file_paths.go`.
 */
export const EMPTY_VALUE_MARKER = 'NA';

const hasNestedFields = (field: FormField): boolean =>
  (field.type === 'array' || field.type === 'object') && Boolean(field.fields?.length);

/**
 * Matches format-mask placeholders like "MM/DD/YYYY", "MM-DD-YYYY" or "YYYY/MM/DD" —
 * UI input hints, not valid values. A sample file must never carry these as example
 * data: uploaded back unchanged they pass schema validation (date fields carry no
 * pattern) and land as literal case data.
 */
const DATE_MASK_PATTERN = /^[MDY]{1,4}([/\-.])[MDY]{1,4}\1[MDY]{2,4}$/i;

/** Example values written to the sample file for date fields, in place of the mask. */
const SAMPLE_DOB = '01/15/1985';
const SAMPLE_DATE = '06/30/2026';

/**
 * The example value a sample-file cell carries for this field. Date fields whose
 * placeholder is a format mask (or missing) get a concrete valid date; everything
 * else keeps its schema placeholder.
 */
const exampleValueFor = (field: FormField): string | undefined => {
  if (field.type === 'date' || field.type === 'dob') {
    if (!field.placeholder || DATE_MASK_PATTERN.test(field.placeholder.trim())) {
      return field.type === 'dob' ? SAMPLE_DOB : SAMPLE_DATE;
    }
  }
  return field.placeholder;
};

/**
 * Flattens schema field groups into the flat column list a campaign file uses.
 *
 * Nested array/object fields contribute their *leaves* under a dotted id
 * (`cpt_codes.code`), never the bare container name — a container has no cell of its
 * own. This mirrors the server's `collectRequiredFields` / `buildSchemaIndex`, so the
 * generated template, the client-side header check, and the parser all agree on the
 * column set.
 *
 * A repeated field with no children (an array of scalars) is itself a leaf: its column
 * holds delimiter-separated values directly.
 */
export const flattenSchemaFields = (fields: FormField[], prefix = ''): SampleFieldDefinition[] => {
  const flattened: SampleFieldDefinition[] = [];

  fields.forEach(field => {
    const id = prefix ? `${prefix}.${field.id}` : field.id;

    if (hasNestedFields(field)) {
      flattened.push(...flattenSchemaFields(field.fields ?? [], id));
      return;
    }

    flattened.push({
      id,
      placeholder: exampleValueFor(field),
      // Convert option values to strings for Excel template compatibility
      options: field.options?.map(opt => ({
        value: String(opt.value),
        label: opt.label,
      })),
      required: field.validation?.required === true,
      conditionallyRequired: Boolean(field.requiredWhenField),
      repeated: Boolean(prefix) || field.type === 'array',
    });
  });

  return flattened;
};

/**
 * True when any column accepts delimiter-separated values, i.e. the workflow has at
 * least one repeated field. Drives whether the upload dialog explains the convention.
 */
export const hasRepeatedFields = (fields: SampleFieldDefinition[]): boolean =>
  fields.some(field => field.repeated);

/**
 * Column the server reads as the case's reference ID instead of as input data.
 * Must stay in sync with the header match in `processCSVFile`/`processExcelFile`
 * (`service/file_processor.go`).
 */
const REFERENCE_ID_COLUMN = 'reference_id';

/** Workflow types whose campaign sample carries an optional `reference_id` column. */
const REFERENCE_ID_SAMPLE_WORKFLOW_TYPES = new Set<string>([
  WorkflowType.PA_STATUS_PBM,
  WorkflowType.PA_STATUS_MEDBV,
]);

/**
 * Prepends an optional `reference_id` column to the campaign sample for workflow types
 * that follow up on an external record. The column is optional — rows left blank fall
 * back to the workflow's `reference_id_path` server-side — so it is only added to the
 * sample, never to the headers an upload must contain.
 */
export const withReferenceIdColumn = (
  fields: SampleFieldDefinition[],
  workflowType?: string,
): SampleFieldDefinition[] => {
  if (!workflowType || !REFERENCE_ID_SAMPLE_WORKFLOW_TYPES.has(workflowType)) return fields;
  if (fields.some(f => f.id.toLowerCase() === REFERENCE_ID_COLUMN)) return fields;
  return [{ id: REFERENCE_ID_COLUMN, placeholder: 'REF-0001' }, ...fields];
};

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

/**
 * Stringifies one value of a repeated cell. Literal pipes are escaped so the
 * server's delimiter split doesn't cut the value apart; flat (non-repeated) cells
 * are never split, so they keep their pipes unescaped.
 */
const repeatedCellPart = (value: unknown): string => String(value).replaceAll('|', '\\|');

/** Walks `segments` down a plain-object tree; undefined as soon as the path breaks. */
const valueAtPath = (node: unknown, segments: string[]): unknown => {
  let current: unknown = node;
  for (const segment of segments) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
};

/**
 * Resolves a flattened campaign column (dotted schema path) against a case's
 * input_data, producing the cell exactly as the campaign parser expects it back:
 *
 * - A path crossing a repeated field yields one value per element, joined with
 *   MULTI_VALUE_DELIMITER; an element missing this leaf contributes
 *   EMPTY_VALUE_MARKER so positions stay zipped with the sibling columns.
 * - An empty/missing array — or one where no element carries the leaf — is an
 *   empty cell.
 * - A flat path yields the value verbatim (no escaping: flat cells aren't split).
 *
 * Inverse of the server's repeated-cell parsing in `service/file_paths.go`.
 */
export const campaignCellValue = (inputData: Record<string, unknown>, columnId: string): string => {
  const segments = columnId.split('.');
  let node: unknown = inputData;

  for (let i = 0; i < segments.length; i++) {
    if (node === null || node === undefined || typeof node !== 'object') {
      return '';
    }
    node = (node as Record<string, unknown>)[segments[i]];

    if (Array.isArray(node)) {
      const tail = segments.slice(i + 1);
      const values = node.map(element => (tail.length ? valueAtPath(element, tail) : element));
      if (values.every(isBlank)) {
        return '';
      }
      return values
        .map(value => (isBlank(value) ? EMPTY_VALUE_MARKER : repeatedCellPart(value)))
        .join(MULTI_VALUE_DELIMITER);
    }
  }

  return isBlank(node) || typeof node === 'object' ? '' : String(node);
};

/**
 * Builds one campaign-file row for a case: one cell per flattened column, resolved
 * from the case's input_data. Pairs with `flattenSchemaFields` for the headers.
 */
export const buildCampaignRow = (
  columns: SampleFieldDefinition[],
  inputData: Record<string, unknown> | undefined,
): string[] => columns.map(column => campaignCellValue(inputData ?? {}, column.id));
